import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, lastValueFrom } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputGroup } from 'primeng/inputgroup';
import { InputText } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../@core/services/modal/modal.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { VatIdCheckResult } from '../../../generated/generated-types';
import { VatIdVerificationService } from '../../crm/services/vat-id-verification.service';
import { VatIdHistoryDialogComponent } from './vat-id-history-dialog.component';
import { VatVerificationRow } from './vat-id-report-dialog.component';
import type { VatCheckTarget } from './formly-vat-id-input.types';

type BadgeState = 'valid' | 'invalid' | 'error' | 'none';

/** Host detail component (address page / document widget), reached via formState.detailComponent, able to persist itself. */
interface VatIdSaveHost {
  save(): Promise<unknown>;
  /** Document widget only: re-evaluate the output-gate indicator after a check, so the block triangle
   * reflects the fresh result without a page reload. Absent on the master-Kunde host. */
  refreshVatGate?(): void | Promise<void>;
  /** Emits after the host persists itself, so the badge can reload post-Save. */
  onSaved?: Observable<unknown>;
}

@Component({
  selector: 'app-formly-vat-id-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './formly-vat-id-input.component.html',
  styleUrls: ['./formly-vat-id-input.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    InputGroup,
    InputText,
    ButtonModule,
    MenuModule,
    TooltipModule,
  ],
})
export class FormlyVatIdInput extends FieldType implements OnInit {
  private readonly vatIdVerificationService = inject(VatIdVerificationService);
  private readonly notificationService = inject(NotificationService);
  private readonly i18nService = inject(I18nService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  /** Anchor id the badge was last loaded for — collapses form.valueChanges to one query per id. */
  private loadedForId?: string;
  /** In-flight host save, so a blur-save and a check-save coalesce into one (no concurrent double-save). */
  private hostSavePromise?: Promise<void>;

  readonly latest = signal<VatVerificationRow | null>(null);
  readonly isChecking = signal(false);
  /** Mirrors the field value so the badge can react to edits; a result only shows while it matches the current number. */
  readonly currentValue = signal<string>('');

  readonly dataTestId = computed(() => this.field?.props?.attributes?.['data-testid'] ?? 'formly-vat-id');

  readonly badgeState = computed<BadgeState>(() => {
    const row = this.latest();
    if (!row || this.normalizeVat(row.requestedVatId) !== this.normalizeVat(this.currentValue())) return 'none';
    if (row.result === VatIdCheckResult.VALID) return 'valid';
    if (row.result === VatIdCheckResult.INVALID) return 'invalid';
    if (row.result === VatIdCheckResult.ERROR) return 'error';
    return 'none';
  });

  private normalizeVat(value?: string | null): string {
    return (value ?? '').replace(/\s/g, '').toUpperCase();
  }

  // Stable array reference: a method/computed bound via [model]="menuItems()" would rebuild the
  // array (and command closures) on every CD pass, which desyncs the PrimeNG overlay mid-click.
  // disabled is refreshed at open time in toggleMenu instead.
  readonly menuItems: MenuItem[] = [
    {
      label: this.i18nService.translate(_('address.vat-check-online')),
      icon: 'fas fa-rotate',
      datatestId: 'vat-check-online-item',
      command: () => this.runOnlineCheck(),
    },
    {
      label: this.i18nService.translate(_('address.vat-check-history')),
      icon: 'fas fa-clock-rotate-left',
      datatestId: 'vat-check-history-item',
      command: () => this.openHistory(),
    },
  ];

  canCheck(): boolean {
    return !!this.anchorId && !this.isChecking();
  }

  /** Hover hint: confirmed, invalid, or check-failed — empty while there is no matching result. */
  readonly vatTooltip = computed(() => {
    const state = this.badgeState();
    switch (state) {
      case 'valid':
        return this.i18nService.translate(_('address.vat-result-valid'));
      case 'invalid':
        return this.i18nService.translate(_('address.vat-result-invalid'));
      case 'error':
        return this.i18nService.translate(_('address.vat-result-error'));
      case 'none':
        return '';
      default: {
        const _exhaustive: never = state;
        return _exhaustive;
      }
    }
  });

  toggleMenu(event: Event, menu: Menu): void {
    const noAnchor = !this.anchorId;
    this.menuItems[0].disabled = noAnchor || this.isChecking();
    this.menuItems[1].disabled = noAnchor;
    menu.toggle(event);
  }

  private openHistory(): void {
    const id = this.anchorId;
    if (!id) return;
    this.modalService
      .fromComponent(VatIdHistoryDialogComponent, {
        size: 'lg',
        closable: true,
        title: this.i18nService.translate(_('address.vat-check-history')),
        locals: { anchorId: id, target: this.vatCheckTarget },
      })
      .subscribe();
  }

  /** 'address' (master Kunde page, default) or 'postalAddress' (document forms), set via field props. */
  private get vatCheckTarget(): VatCheckTarget {
    return (this.props?.['vatCheckTarget'] as VatCheckTarget) ?? 'address';
  }

  /** id of the entity the vatId belongs to (Address or PostalAddress) — both expose it as model.id. */
  private get anchorId(): string | undefined {
    return (this.model as { id?: string })?.id;
  }

  ngOnInit(): void {
    // anchorId (model.id) is not reactive and may arrive after init on in-app navigation, so load
    // once now (browser-reload case) and again when the document patches the vatId control in.
    this.currentValue.set(this.formControl.value ?? '');
    this.loadBadgeIfNeeded();
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.currentValue.set(value ?? '');
        this.loadBadgeIfNeeded();
      });

    // Reload the badge after a host Save (defer a tick so the persisted anchor id propagates in).
    const host = this.formState?.detailComponent as VatIdSaveHost | undefined;
    host?.onSaved?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadedForId = undefined;
      setTimeout(() => this.loadBadgeIfNeeded());
    });
  }

  private loadBadgeIfNeeded(): void {
    const id = this.anchorId;
    if (!id || id === this.loadedForId) return;
    this.loadedForId = id;
    this.latestFor(id).subscribe((latest) => this.latest.set(latest ?? null));
  }

  private latestFor(id: string): Observable<VatVerificationRow | null> {
    if (this.vatCheckTarget === 'postalAddress') {
      return this.vatIdVerificationService
        .getLatestWithinWindow(id)
        .mapSingle((data) => data.getLatestVatIdVerificationWithinWindow);
    }
    return this.vatIdVerificationService
      .getLatestWithinWindowForAddress(id)
      .mapSingle((data) => data.getLatestVatIdVerificationWithinWindowForAddress);
  }

  /** Persist the host entity (address page / document) when the vatId changed, so a check reads fresh data. Returns whether a save ran. */
  private async saveHostIfDirty(): Promise<boolean> {
    if (this.hostSavePromise) {
      await this.hostSavePromise;
      return false;
    }
    if (!this.formControl?.dirty) return false;
    const host = this.formState?.detailComponent as VatIdSaveHost | undefined;
    if (!host?.save) return false;
    this.hostSavePromise = host.save().then(() => undefined);
    try {
      await this.hostSavePromise;
    } finally {
      this.hostSavePromise = undefined;
    }
    return true;
  }

  /** On blur, autosave the host so the entered number is persisted before the user opens the check menu. */
  async onVatIdBlur(): Promise<void> {
    try {
      if (await this.saveHostIfDirty()) {
        this.loadedForId = undefined;
        this.loadBadgeIfNeeded();
      }
    } catch (error: any) {
      console.debug('vat-id blur autosave skipped:', error?.message);
    }
  }

  private async runOnlineCheck(): Promise<void> {
    if (this.isChecking()) return;
    if (!this.formControl?.value) {
      this.notificationService.warning(_('address.vat-enter-id-first'));
      return;
    }
    this.isChecking.set(true);
    try {
      await this.saveHostIfDirty();
      const id = this.anchorId;
      if (!id) return;
      this.applyCheckResult(await this.performCheck(id));
      // Let the host (document widget) refresh its output-gate indicator so the block triangle
      // reflects this result immediately; no-op on the master-Kunde host.
      await (this.formState?.detailComponent as VatIdSaveHost | undefined)?.refreshVatGate?.();
    } catch (error: any) {
      this.notificationService.error(error?.message ?? this.i18nService.translate(_('address.vat-result-error')));
    } finally {
      this.isChecking.set(false);
    }
  }

  private async performCheck(id: string): Promise<VatVerificationRow> {
    if (this.vatCheckTarget === 'postalAddress') {
      return (await lastValueFrom(this.vatIdVerificationService.checkVatId(id))).checkVatId;
    }
    return (await lastValueFrom(this.vatIdVerificationService.checkVatIdForAddress(id))).checkVatIdForAddress;
  }

  private applyCheckResult(verification: VatVerificationRow): void {
    this.latest.set(verification);
    if (verification.result === VatIdCheckResult.VALID) {
      this.notificationService.success(_('address.vat-result-valid'));
    } else if (verification.result === VatIdCheckResult.INVALID) {
      this.notificationService.error(_('address.vat-result-invalid'));
    } else {
      this.notificationService.warning(_('address.vat-result-error'));
    }
  }
}
